import { type Except, type Optional, ReactUtils } from '@festina-life/core'
import cx from 'classnames'
import type { ComponentProps, RefObject } from 'react'

import { rawTextInputStyles } from '../textInput/rawTextInputStyles'

export type RawTextAreaProps = Except<
  ComponentProps<'textarea'>,
  'rows' | 'cols' // we give a sensible default with autosizing
> & {
  isInError?: boolean
  value?: string
  editable?: boolean
  disabled?: boolean
  className?: Optional<string>
  isHidden?: boolean
  rows?: number
  /**
   * Whether the text area should automatically adjust its height based on the content.
   * Constrained to max 15 lines by default
   */
  isAutoSizing?: boolean
  ref?: RefObject<HTMLTextAreaElement>
}

/** Text area that can be used in Festina Forms to display a text area that follows our styling rules. May or may
 * not be wrapped by a FormInput component to add labels, descriptions and error messages around the input field.
 */
export const RawTextArea = ({
  isInError,
  disabled,
  editable = true,
  value,
  className,
  isHidden,
  isAutoSizing = true,
  ref,
  rows = 4,
  ...other
}: RawTextAreaProps) => {
  const valueToShow = value ?? (other.defaultValue ? undefined : '')
  const styles = cx(
    rawTextInputStyles({
      disabled: !!disabled,
      isInError: !!isInError,
      isAfterAddOn: false,
    }),
    'whitespace-pre-wrap', // ensure long text wraps into multiple lines instead of overflowing horizontally
    'max-w-inputs-and-alerts', // ensure text areas don't get rediculously wide when they have a lot of content in one line
    isAutoSizing ? 'resize-none col-start-1 row-start-1' : 'resize min-w-full',
    className,
  )
  return (
    <>
      {editable ? (
        <ReactUtils.ConditionalWrap
          condition={isAutoSizing}
          wrap={(children) => (
            <div className="relative w-full grid">
              {children}
              <div
                className={cx(
                  styles,
                  'line-clamp-15 pointer-events-none min-h-0 invisible',
                )}
              >
                {valueToShow}
                {
                  '\n\u0020' /* Extra content to add new empty lines visually before reaching the end */
                }
              </div>
            </div>
          )}
        >
          <textarea
            className={cx(styles, 'overflow-auto')}
            data-autofocus
            rows={rows}
            aria-invalid={isInError}
            disabled={disabled}
            ref={ref}
            value={valueToShow}
            {...other}
          />
        </ReactUtils.ConditionalWrap>
      ) : (
        <div className="w-full whitespace-pre-wrap p-2">{value}</div>
      )}
    </>
  )
}
